<script setup lang="ts">
import { nextTick, ref } from 'vue';
import { HIT_VERDICT_LABELS, type HitVerdict } from './caseDetail';

const props = withDefaults(
  defineProps<{
    hitId: string;
    comment?: string;
    override?: HitVerdict;
    kbEnabled?: boolean;
  }>(),
  { kbEnabled: true }
);

const emit = defineEmits<{
  'select-verdict': [hitId: string, verdict: HitVerdict];
  'update-comment': [hitId: string, value: string];
}>();

// Collapsed by default, matching v4 — the textarea only appears inline
// above these buttons once the reviewer opens it (Note button or "C"),
// pre-filled with whatever's already saved. A persisted note still shows
// separately in the page's own "Reviewer note" card regardless of this
// state — the two aren't mutually exclusive in the design either.
const composing = ref(false);
const draft = ref('');
const textareaRef = ref<HTMLTextAreaElement>();

function openComposer() {
  composing.value = true;
  draft.value = props.comment ?? '';
  nextTick(() => textareaRef.value?.focus());
}
function cancelComposer() {
  composing.value = false;
}
function saveComposer() {
  emit('update-comment', props.hitId, draft.value);
  composing.value = false;
}

// closeComposer is cancelComposer under a name that reads sensibly from
// the outside — the page's Escape handler calls this to match v4's own
// Escape-while-typing behavior (blur *and* drop out of composing), not
// just this component's own Cancel button.
defineExpose({ openComposer, closeComposer: cancelComposer });

const verdictShortcuts: Record<HitVerdict, string> = {
  false_positive: 'F',
  true_positive: 'T',
  unknown: 'U',
};
const verdictOptions: HitVerdict[] = ['true_positive', 'false_positive', 'unknown'];

const activeOptionStyles: Record<HitVerdict, string> = {
  false_positive: 'border-green-600 bg-green-50 text-green-700',
  true_positive: 'border-red-600 bg-red-50 text-red-700',
  unknown: 'border-amber-600 bg-amber-50 text-amber-700',
};
const inactiveOptionStyle = 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50';
</script>

<template>
  <div
    class="flex-none border-t border-gray-200 bg-white p-3"
    style="padding-bottom: calc(0.75rem + env(safe-area-inset-bottom))"
    data-test="hit-note-bar"
  >
    <div v-if="composing" class="mb-2.5">
      <textarea
        ref="textareaRef"
        v-model="draft"
        rows="3"
        placeholder="Why is this a true or false positive? Cite the records you relied on."
        class="w-full resize-y rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none"
      ></textarea>
      <div class="mt-2 flex justify-end gap-2">
        <button
          type="button"
          class="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50"
          @click="cancelComposer"
        >
          Cancel
        </button>
        <button
          type="button"
          class="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700"
          @click="saveComposer"
        >
          Save note
        </button>
      </div>
    </div>
    <div class="flex items-center gap-2">
      <button
        v-for="option in verdictOptions"
        :key="option"
        type="button"
        class="flex flex-1 items-center justify-center gap-1.5 rounded-lg border px-2.5 py-2 text-[12.5px] font-semibold whitespace-nowrap"
        :class="override === option ? activeOptionStyles[option] : inactiveOptionStyle"
        @click="emit('select-verdict', hitId, option)"
      >
        {{ HIT_VERDICT_LABELS[option] }}
        <span
          v-if="kbEnabled"
          class="hidden rounded border border-gray-300 px-1 text-[10px] font-semibold text-gray-500 lg:inline"
          >{{ verdictShortcuts[option] }}</span
        >
      </button>
      <button
        type="button"
        aria-label="Add note"
        class="flex flex-none items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-2.5 py-2 text-[12.5px] font-semibold whitespace-nowrap text-gray-600 hover:bg-gray-50"
        @click="openComposer"
      >
        Note
        <span
          v-if="kbEnabled"
          class="hidden rounded border border-gray-300 px-1 text-[10px] font-semibold text-gray-500 lg:inline"
          >C</span
        >
      </button>
    </div>
  </div>
</template>
